import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBidStream } from "../hooks/useBidStream";
import BidLeaderboard from "../components/BidLeaderboard";
import ComparisonMatrix from "../components/ComparisonMatrix";
import EscrowTimeline from "../components/EscrowTimeline";
import FileUploader from "../components/FileUploader";
import MessagesPanel from "../components/MessagesPanel";
import { ArrowLeft, Send, Sparkles, X, Radio, Plus, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

function LineItemsTable({ items, onChange, currency }) {
  const upd = (idx, key, val) => {
    const next = items.slice();
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  const add = () => onChange([...items, { item: "", qty: 1, unit_price: 0, currency: currency || "USD", included: true, substituted_from: "", substitution_reason: "" }]);
  const rm = (idx) => onChange(items.filter((_, i) => i !== idx));
  const totalUsd = items.reduce((s, li) => s + (Number(li.unit_price || 0) * Number(li.qty || 1)) / (li.currency === "USD" ? 1 : 1), 0);
  return (
    <div data-testid="bid-line-items">
      <div className="overflow-x-auto rounded border border-hairline">
        <table className="ledger-table">
          <thead>
            <tr>
              <th>Item</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Unit price</th>
              <th className="text-right">Subtotal (USD)</th>
              <th>Substituted from</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-6">No line items. Add one to build a per-item quote →</td></tr>
            )}
            {items.map((li, i) => (
              <tr key={i} className={li.substituted_from ? "ledger-cell-flagged" : ""}>
                <td>
                  <input value={li.item} onChange={(e) => upd(i, "item", e.target.value)} className="w-full py-1.5 px-2 text-sm" placeholder="e.g. Ballroom (day rate)" data-testid={`li-item-${i}`} />
                </td>
                <td>
                  <input type="number" min={1} value={li.qty} onChange={(e) => upd(i, "qty", e.target.value)} className="w-20 py-1.5 px-2 text-sm text-right" data-testid={`li-qty-${i}`} />
                </td>
                <td>
                  <div className="flex gap-1">
                    <input type="number" min={0} value={li.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className="w-24 py-1.5 px-2 text-sm text-right" data-testid={`li-price-${i}`} />
                    <select value={li.currency || currency || "USD"} onChange={(e) => upd(i, "currency", e.target.value)} className="text-xs py-1.5 px-1" data-testid={`li-currency-${i}`}>
                      {["USD", "AED", "EUR", "GBP", "INR", "SGD", "THB", "JPY"].map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </td>
                <td className="text-right font-mono text-sm">
                  {li.currency === "USD" ? `$${(Number(li.unit_price || 0) * Number(li.qty || 1)).toFixed(0)}` : <span className="text-muted">FX @ save</span>}
                </td>
                <td>
                  <input value={li.substituted_from || ""} onChange={(e) => upd(i, "substituted_from", e.target.value)} className="w-full py-1.5 px-2 text-sm" placeholder="If different from RFP" data-testid={`li-sub-${i}`} />
                </td>
                <td>
                  <button type="button" onClick={() => rm(i)} className="text-muted hover:text-red-600 p-1" data-testid={`li-remove-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3">
        <button type="button" onClick={add} className="btn-ghost text-sm" data-testid="add-line-item">
          <Plus className="w-4 h-4" /> Add line item
        </button>
        {items.length > 0 && (
          <span className="font-mono text-sm text-ink">
            Subtotal (USD-only): <strong>${Math.round(totalUsd).toLocaleString()}</strong>
            <span className="text-muted ml-2 text-xs">non-USD converted server-side</span>
          </span>
        )}
      </div>
    </div>
  );
}

function PlaceBidModal({ leadId, lead, onClose, onPlaced }) {
  const isMice = lead?.segment === "mice" || lead?.type === "mice";
  const [form, setForm] = useState({
    amount_usd: 5000,
    inclusions: isMice ? "Conference hall, F&B, AV, gala dinner, group transfers" : "Hotels, transfers, meals",
    timeline_days: 2,
    hotel_category: isMice ? "5-star" : "4-star",
    transfer_type: "private",
    meal_plan: isMice ? "Full Board" : "Breakfast",
    flight_class: "not_included",
    exclusions: "",
    notes: "",
    currency: "USD",
  });
  const [lineItems, setLineItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        amount_usd: Number(form.amount_usd),
        timeline_days: Number(form.timeline_days),
        inclusions: form.inclusions,
        hotel_category: form.hotel_category,
        transfer_type: form.transfer_type,
        meal_plan: form.meal_plan,
        flight_class: form.flight_class,
        inclusions_list: form.inclusions.split(",").map((s) => s.trim()).filter(Boolean),
        exclusions_list: form.exclusions.split(",").map((s) => s.trim()).filter(Boolean),
        line_items: lineItems.map((li) => ({
          item: li.item, qty: Number(li.qty || 1), unit_price: Number(li.unit_price || 0),
          currency: li.currency || form.currency || "USD",
          included: li.included !== false,
          substituted_from: li.substituted_from || null,
          substitution_reason: li.substitution_reason || null,
        })),
        currency: form.currency,
        notes: form.notes,
        attachments,
      };
      const r = await api.post(`/leads/${leadId}/bids`, payload);
      const score = r.data?.apples_score;
      if (score != null) {
        if (score >= 85) toast.success(`Bid submitted · Apple-to-apple match ${score}%`);
        else if (score >= 60) toast.success(`Bid submitted · ${score}% match — minor gaps`);
        else toast.warning(`Bid submitted · ${score}% match. Gaps: ${(r.data.apples_missing || []).slice(0, 3).join(", ")}`);
      } else {
        toast.success("Bid submitted");
      }
      onPlaced();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to bid");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="place-bid-modal">
      <div className="bg-white border border-hairline rounded-md w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-hairline bg-paper sticky top-0 z-10">
          <div>
            <p className="overline">Submit bid</p>
            <h3 className="font-display text-2xl font-bold text-ink mt-1">
              {lead?.title ? <>Quote for <span className="text-brand">{lead.title}</span></> : "Place a bid"}
            </h3>
          </div>
          <button onClick={onClose} className="text-muted hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="overline">Total (USD)</label>
              <input type="number" min={0} required value={form.amount_usd} onChange={upd("amount_usd")} className="w-full mt-2 px-3 py-2.5" data-testid="bid-amount" />
            </div>
            <div>
              <label className="overline">Delivery (days)</label>
              <input type="number" min={1} required value={form.timeline_days} onChange={upd("timeline_days")} className="w-full mt-2 px-3 py-2.5" data-testid="bid-timeline" />
            </div>
            <div>
              <label className="overline">Hotel category</label>
              <select value={form.hotel_category} onChange={upd("hotel_category")} className="w-full mt-2 px-3 py-2.5" data-testid="bid-hotel">
                {["3-star", "4-star", "5-star", "boutique", "luxury"].map((v) => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">Transfers</label>
              <select value={form.transfer_type} onChange={upd("transfer_type")} className="w-full mt-2 px-3 py-2.5" data-testid="bid-transfer">
                {["private", "shared", "luxury", "none"].map((v) => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">Meal plan</label>
              <select value={form.meal_plan} onChange={upd("meal_plan")} className="w-full mt-2 px-3 py-2.5" data-testid="bid-meal">
                {["Room Only", "Breakfast", "Half Board", "Full Board", "All Inclusive"].map((v) => <option key={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">Flight class</label>
              <select value={form.flight_class} onChange={upd("flight_class")} className="w-full mt-2 px-3 py-2.5" data-testid="bid-flight">
                {[{v:"not_included",l:"Not included"},{v:"economy",l:"Economy"},{v:"business",l:"Business"},{v:"first",l:"First"}].map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">Base currency</label>
              <select value={form.currency} onChange={upd("currency")} className="w-full mt-2 px-3 py-2.5" data-testid="bid-currency">
                {["USD", "AED", "EUR", "GBP", "INR", "SGD", "THB", "JPY"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="pt-4 border-t border-hairline">
            <p className="overline text-brand">Line items · per-pax · per-hall · per-day</p>
            <p className="text-xs text-muted mt-1 mb-3">Add row-level pricing so buyers compare apples-to-apples. If a line differs from the RFP, note it under <em>Substituted from</em>.</p>
            <LineItemsTable items={lineItems} onChange={setLineItems} currency={form.currency} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4 border-t border-hairline">
            <div>
              <label className="overline">Inclusions (comma-separated)</label>
              <input required value={form.inclusions} onChange={upd("inclusions")} className="w-full mt-2 px-3 py-2.5" data-testid="bid-inclusions" />
            </div>
            <div>
              <label className="overline">Exclusions</label>
              <input value={form.exclusions} onChange={upd("exclusions")} className="w-full mt-2 px-3 py-2.5" data-testid="bid-exclusions" placeholder="e.g. Tips, Visa fees" />
            </div>
            <div className="md:col-span-2">
              <label className="overline">Notes</label>
              <textarea rows={2} value={form.notes} onChange={upd("notes")} className="w-full mt-2 px-3 py-2.5" data-testid="bid-notes" />
            </div>
          </div>

          <div className="pt-4 border-t border-hairline">
            <p className="overline text-brand">Attachments · proposal PDF, hotel factsheets, floor plans</p>
            <div className="mt-3">
              <FileUploader value={attachments} onChange={setAttachments} purpose="attachment" testid="bid-attachments" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-hairline">
            <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="submit-bid">
              <Send className="w-4 h-4" /> {busy ? "Sending…" : "Submit bid"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [escrow, setEscrow] = useState(null);
  const [aiSummary, setAiSummary] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [showBid, setShowBid] = useState(false);

  const load = () => {
    api.get(`/leads/${id}`).then((r) => {
      setData(r.data);
      setAiSummary(r.data.lead?.ai_summary || "");
    });
    api.get("/escrow/me").then((r) => {
      const match = (r.data || []).find((e) => e.lead_id === id);
      setEscrow(match || null);
    });
  };
  useEffect(load, [id]);

  useBidStream(id, (evt) => {
    if (evt?.data?.dmc_id === user?.user_id) return;
    toast.success(`New bid · $${Number(evt.data?.amount_usd || 0).toLocaleString()} from ${evt.data?.dmc_name}`);
    load();
  });

  if (!data) return (
    <div className="App"><Navbar /><div className="max-w-7xl mx-auto px-6 py-20 text-muted" data-testid="lead-loading">Loading…</div></div>
  );
  const { lead, bids, agent } = data;
  const isAgent = user?.role === "agent" && lead.agent_id === user.user_id;
  const isDmc = user?.role === "dmc" || user?.role === "mice_operator";
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const runAI = async () => {
    setAiBusy(true);
    try {
      const r = await api.post(`/ai/match/${id}`);
      setAiSummary(r.data.summary || "");
      load();
      toast.success("AI evaluation refreshed");
    } catch (e) { toast.error("AI request failed"); }
    finally { setAiBusy(false); }
  };

  const onAccept = async (bid) => {
    if (!confirm(`Accept bid from ${bid.dmc_name}?`)) return;
    try {
      await api.post(`/bids/${bid.bid_id}/accept`);
      toast.success("Bid accepted · escrow held");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not accept"); }
  };

  return (
    <div className="App" data-testid="lead-detail-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <Link to="/dashboard" className="text-sm text-muted inline-flex items-center gap-2 hover:text-ink mb-6" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card-paper p-6 md:p-8">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="overline">{lead.segment || lead.type} · {lead.destination}</p>
                  <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-ink mt-3" data-testid="lead-title">{lead.title}</h1>
                </div>
                <span className={`text-xs font-mono uppercase tracking-widest px-3 py-1 rounded border ${
                  lead.status === "open" ? "text-brand border-brand" : "text-verified border-verified"
                }`}>{lead.status}</span>
              </div>
              <p className="text-muted mt-5 leading-relaxed">{lead.description}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-hairline">
                <div><p className="overline">Pax</p><p className="font-mono text-ink text-lg mt-1">{lead.pax}</p></div>
                <div><p className="overline">Days</p><p className="font-mono text-ink text-lg mt-1">{lead.duration_days}</p></div>
                <div><p className="overline">Budget</p><p className="font-mono text-ink text-lg mt-1">${Number(lead.budget_usd).toLocaleString()}</p></div>
                <div><p className="overline">Travel</p><p className="font-mono text-ink text-lg mt-1">{lead.travel_date}</p></div>
              </div>
              {(lead.hall_capacity || lead.hall_layout || lead.gala_dinner || lead.team_building) && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-hairline">
                  {lead.hall_capacity && (<div><p className="overline">Hall pax</p><p className="font-mono text-ink text-sm mt-1">{lead.hall_capacity}</p></div>)}
                  {lead.hall_layout && (<div><p className="overline">Layout</p><p className="font-mono text-ink text-sm mt-1">{lead.hall_layout}</p></div>)}
                  {lead.gala_dinner && (<div><p className="overline">Gala</p><p className="font-mono text-verified text-sm mt-1">✓ included</p></div>)}
                  {lead.team_building && (<div><p className="overline">Team-building</p><p className="font-mono text-verified text-sm mt-1">✓ included</p></div>)}
                </div>
              )}
              {lead.av_requirements && (
                <div className="mt-4 pt-4 border-t border-hairline">
                  <p className="overline">AV requirements</p>
                  <p className="text-sm text-ink mt-2">{lead.av_requirements}</p>
                </div>
              )}
              {lead.inclusions_checklist?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-5">
                  {lead.inclusions_checklist.map((r) => (
                    <span key={r} className="text-[10px] font-mono uppercase tracking-wider text-ink border border-hairline px-2 py-1 rounded">{r}</span>
                  ))}
                </div>
              )}
              {lead.attachments?.length > 0 && (
                <div className="mt-5 pt-5 border-t border-hairline">
                  <p className="overline">RFP attachments</p>
                  <ul className="mt-3 space-y-1.5">
                    {lead.attachments.map((f) => (
                      <li key={f.file_id}>
                        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${f.file_id}?auth=${token || ""}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-brand hover:underline">
                          <FileText className="w-4 h-4" /> {f.original_filename}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
                {isDmc && lead.status === "open" && (
                  <button onClick={() => setShowBid(true)} className="btn-primary text-sm" data-testid="place-bid-btn">
                    <Send className="w-4 h-4" /> Place bid
                  </button>
                )}
                <button onClick={runAI} disabled={aiBusy} className="btn-ghost text-sm" data-testid="run-ai-btn">
                  <Sparkles className="w-4 h-4" /> {aiBusy ? "Evaluating…" : "Run AI evaluation"}
                </button>
              </div>
            </div>

            {aiSummary && (
              <div className="card-ledger p-6 border-l-4 border-brand" data-testid="ai-summary-card">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-brand" />
                  <p className="overline">Claude · Bid evaluation</p>
                </div>
                <p className="text-ink mt-3 leading-relaxed">{aiSummary}</p>
              </div>
            )}

            <div>
              <h2 className="font-display text-xl font-bold text-ink mb-4 flex items-center gap-2">Bid leaderboard
                <span className="text-muted text-sm font-mono">({bids.length})</span>
                <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-mono text-muted" data-testid="bid-live-indicator">
                  <Radio className="w-3.5 h-3.5 text-brand animate-pulse" /> LIVE
                </span>
              </h2>
              <BidLeaderboard bids={bids} canAccept={isAgent && lead.status === "open"} onAccept={onAccept} />
            </div>

            {bids.length > 1 && (user?.role === "agent" || user?.role === "admin") && (
              <ComparisonMatrix bids={bids} />
            )}

            {/* THREADS */}
            <MessagesPanel lead={lead} bids={bids} />
          </div>

          {/* Aside */}
          <aside className="space-y-6">
            <div className="card-paper p-6">
              <p className="overline">Posted by</p>
              <p className="font-display text-lg text-ink mt-2">{agent?.name || "—"}</p>
              <p className="text-sm text-muted">{agent?.company} · {agent?.country || "—"}</p>
              <div className="text-xs font-mono text-muted mt-3">{new Date(lead.created_at).toLocaleString()}</div>
            </div>
            <div className="card-paper p-6" data-testid="platform-fee-card">
              <p className="overline">Platform fee</p>
              <p className="font-display text-2xl font-bold text-ink mt-2">
                ${lead.pax} <span className="text-muted text-sm font-normal">on close</span>
              </p>
              <p className="text-xs text-muted mt-2 font-mono">$1 × {lead.pax} pax</p>
            </div>
            {escrow ? (
              <EscrowTimeline escrow={escrow} onUpdated={load} />
            ) : (
              <div className="card-paper p-6 text-sm text-muted" data-testid="no-escrow">
                Escrow appears here once a bid is accepted.
              </div>
            )}
          </aside>
        </div>
      </div>
      {showBid && <PlaceBidModal leadId={id} lead={lead} onClose={() => setShowBid(false)} onPlaced={load} />}
    </div>
  );
}
